import { useRef, useState } from 'react';
import React from 'react';
import { Registration } from '../API';
import { isIndividual } from '../utils/partyAttendance';
import { generateAttendeeCsv, downloadCsv } from '../utils/csv';

export function useAttendeeSelection(
  attendees: Registration[],
  setAttendees: React.Dispatch<React.SetStateAction<Registration[]>>,
  filteredAttendees: Array<{ registrationId: string }>,
  onCheckIn: (count: number, attendee: Registration) => Promise<void>,
) {
  const [selectedAttendees, setSelectedAttendees] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState('');
  const inFlight = useRef(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const selected = attendees.filter(a => selectedAttendees.has(a.registrationId));
  const allowUndo = !selected.some(isIndividual);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [selectedDownloadFormat, setSelectedDownloadFormat] = useState('csv');

  const toggleSelectAll = (checked: boolean) => {
    if (inFlight.current) return;
    if (checked) {
      const allIds = filteredAttendees.map(a => a.registrationId);
      setSelectedAttendees(new Set(allIds));
    } else {
      setSelectedAttendees(new Set());
    }
  };

  const toggleSelectOne = (id: string) => {
    if (inFlight.current) return;
    const newSet = new Set(selectedAttendees);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setSelectedAttendees(newSet);
  };

  const handleBulkSubmit = async () => {
    if (inFlight.current) return;
    setBulkError(null);
    if (!selected.length) {
      setBulkError('Select at least one registration.');
      return;
    }
    if (bulkAction === 'CHECK_IN' || bulkAction === 'UNDO_CHECK_IN') {
      if (bulkAction === 'UNDO_CHECK_IN' && !allowUndo) {
        setBulkError('Undo individual attendance from each person’s row. No registrations were changed.');
        return;
      }
      const message = bulkAction === 'CHECK_IN'
        ? `Check in all people in ${selected.length} selected registration(s)?`
        : `Undo check-in for ${selected.length} selected registration(s)?`;
      if (!window.confirm(message)) return;
      inFlight.current = true;
      setBulkBusy(true);
      const completed = new Set<string>();
      const failures: string[] = [];
      try {
        // Each party has its own command/receipt. Preserve failed selections and
        // leave uncertain individual commands available through the row retry.
        for (const attendee of selected) {
          try {
            await onCheckIn(bulkAction === 'CHECK_IN' ? Math.max(1, attendee.guests ?? 1) : 0, attendee);
            completed.add(attendee.registrationId);
          } catch {
            failures.push(`${attendee.firstName ?? ''} ${attendee.lastName ?? ''}`.trim() || attendee.registrationId);
          }
        }
        setSelectedAttendees(previous => new Set([...previous].filter(id => !completed.has(id))));
        if (failures.length) setBulkError(`Could not confirm: ${failures.join(', ')}. Review those registrations; use Retry last action on any row with an uncertain result. Completed registrations were deselected.`);
      } finally {
        inFlight.current = false;
        setBulkBusy(false);
      }
    } else if (bulkAction === 'SUBMIT_DATA') {
      alert('Data upload successful. Selected attendees have been marked to be submitted to CRM by end of day.');
      setAttendees(prev =>
        prev.map(attendee =>
          selectedAttendees.has(attendee.registrationId)
            ? { ...attendee, submittedToCRM: true }
            : attendee
        )
      );
    } else if (bulkAction === 'DOWNLOAD') {
      setShowDownloadModal(true);
    }
  };

  const handleDownloadConfirm = () => {
    if (selectedDownloadFormat === 'csv') {
      const selectedData = attendees.filter(att => selectedAttendees.has(att.registrationId));
      if (selectedData.length === 0) {
        alert('No attendees selected for download.');
        setShowDownloadModal(false);
        return;
      }
      const csvContent = generateAttendeeCsv(selectedData);
      downloadCsv(csvContent, 'attendees.csv');
    } else if (selectedDownloadFormat === 'excel') {
      alert('Excel download is not yet implemented.');
    } else {
      alert('Invalid download format specified.');
    }
    setShowDownloadModal(false);
  };

  return {
    selectedAttendees, bulkBusy, bulkError, allowUndo,
    bulkAction, setBulkAction,
    showDownloadModal, setShowDownloadModal,
    selectedDownloadFormat, setSelectedDownloadFormat,
    toggleSelectAll,
    toggleSelectOne,
    handleBulkSubmit,
    handleDownloadConfirm,
  };
}
